import React from "react"

import { useTranslation } from "react-i18next"

import { Box } from "@mui/material"
import Grid from "@mui/material/Grid"
import Skeleton from "@mui/material/Skeleton"
import type { BarDatum } from "@nivo/bar"
import { ResponsiveBar } from "@nivo/bar"
import { TooltipWrapper } from "@nivo/tooltip"
import { Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

import { getTheme } from "../../../hooks/UseTheme"
import { NivoTheme } from "../../../themes/nivoTheme"
import type { SummaryByDay } from "../../../types/generated/api/SummaryByDay"
import { fillMissingRequestSummaryDates } from "../../../utils/deploymentDetailsHelpers"

import styles from "./RequestsDailyBarChart.module.scss"

type Props = {
  data: SummaryByDay[]
  isLoading: boolean
  graphHeight: number
}

export function RequestsDailyBarChart(props: Readonly<Props>): React.ReactElement {
  const { data, isLoading, graphHeight } = props

  const theme = getTheme()

  const { t } = useTranslation("common")

  const adjustedData: SummaryByDay[] = React.useMemo(() => {
    if (data && data.length > 0) {
      const newData = Array.from(data)
      const startDate = moment().subtract(27, "days")
      const endDate = moment()
      //map creation date to each model
      const finalData = fillMissingRequestSummaryDates(startDate, endDate, newData)
      return finalData
    }
    return []
  }, [data])

  return (
    <Grid className={styles.paper}>
      {isLoading || !data ? (
        <Skeleton animation="wave" height={60} />
      ) : (
        <Box style={{ height: graphHeight, padding: 0, margin: 0 }} className={styles.wrapper}>
          <ResponsiveBar
            data={adjustedData as BarDatum[]}
            theme={NivoTheme(theme)}
            indexBy="timestamp"
            keys={["alternateSuccessful", "alternateFailed", "empty"]}
            colors={[theme.palette.green.background[1], theme.palette.red.background[1], theme.palette.base.border[1]]}
            colorBy="id"
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            borderWidth={0}
            borderRadius={2}
            padding={0.4}
            layout="vertical"
            valueScale={{ type: "linear" }}
            axisTop={null}
            axisRight={null}
            axisLeft={null}
            enableLabel={false}
            enableGridY={false}
            animate={true}
            tooltip={(slice) => {
              return (
                <TooltipWrapper anchor={"left"} position={[-10, 0]}>
                  <Grid sx={{ pointerEvents: "none" }}>
                    <Grid className={"chart-tooltip"}>
                      <Grid container direction={"row"} alignItems="center" spacing={1}>
                        <Grid item>
                          <Typography noWrap variant="label-bold" display="inline" variantColor={2}>
                            {slice.data.timestamp}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid item container xs={12} direction={"row"} alignItems={"flex-start"} wrap="nowrap">
                        <span key="chip" className={`${styles.block} ${styles.successful}`} />
                        <Typography
                          style={{ textAlign: "start", paddingTop: "2px" }}
                          noWrap
                          variant="label-bold"
                          variantColor={2}
                        >
                          {t("barChart.successful")}: {slice.data.successful ?? 0}
                        </Typography>
                      </Grid>

                      <Grid item container xs={12} direction={"row"} alignItems={"flex-start"}>
                        <Grid item>
                          <span
                            style={{ display: "block" }}
                            key="chip"
                            className={`${styles.block} ${styles.failed}`}
                          />
                        </Grid>
                        <Typography
                          style={{ textAlign: "start", paddingTop: "2px" }}
                          noWrap
                          variant="label-bold"
                          variantColor={2}
                        >
                          {t("barChart.failed")}: {slice.data.failed ?? 0}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </TooltipWrapper>
              )
            }}
          />
        </Box>
      )}
    </Grid>
  )
}
